import React, { useCallback, useMemo, useState } from "react";
import {
  DocumentShareIcon,
  DollarIcon,
  Heading,
  Pane,
  TickIcon,
  toaster,
  TrashIcon,
} from "evergreen-ui";
import Fieldset from "./Fieldset";
import FilePreview from "../FilePreview";
import VendorInput from "./VendorInput";
import InfoInput from "./InfoInput";
import AccountingInput from "./AccountingInput";
import MatchInput from "./MatchInput";
import ApproversInput from "./ApproversInput";
import InvoiceDeleteButton from "./InvoiceDeleteButton";
import PromiseButton from "../PromiseButton";
import useControlHub from "../../hooks/useControlHub";
import InvoicesPayDialog from "../InvoicesPayDialog";
import StatusBadge from "../StatusBadge";
import APStatus from "./APStatus";
import BillStateIndicator from "./BillStateIndicator";
import useBillForm from "./useBillForm";
import {
  BILL_APPROVE_STATUS,
  BILL_PAYMENT_STATUS,
} from "../../utils/constants";
import { APP_URL } from "../../config";

const InvoiceFormModule = ({ id }) => {
  const { api, company } = useControlHub();
  const { bill, billState, BILL_STATES, loadBill, saveToBill } =
    useBillForm(id);
  const [dialog, setDialog] = useState(null);

  const badgeStatus = useMemo(() => {
    if (
      bill?.status === BILL_APPROVE_STATUS.approved &&
      bill?.paymentInfo?.paid_status !== BILL_PAYMENT_STATUS.unpaid
    ) {
      return "paid";
    }
    return bill?.status;
  }, [bill]);

  const sendBillToAPSystem = useCallback(async () => {
    return new Promise((resolve, reject) => {
      api
        .sendBillToAPSystem({ billID: id })
        .then((data) => {
          if (!data.success) {
            throw new Error(
              `Unable to send to ${
                company.features.apInboxName || "AP System"
              }: ` + (data.message || "-")
            );
          }
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }, [api, company.features.apInboxName, id]);

  const payBill = useCallback(async () => {
    return new Promise((resolve, reject) => {
      const handleClose = () => {
        setDialog(null);
        reject();
      };
      const handlePayFailure = (error) => {
        setDialog(null);
        reject(error);
      };
      const handlePaySuccess = () => {
        setDialog(null);
        resolve();
      };
      setDialog(
        <InvoicesPayDialog
          invoices={[bill]}
          onClose={handleClose}
          onFailure={handlePayFailure}
          onSuccess={handlePaySuccess}
        />
      );
    });
  }, [bill]);

  const submitBill = useCallback(async () => {
    return new Promise((resolve, reject) => {
      api
        .submitBill({ billID: id })
        .then((data) => {
          if (!data.success) {
            throw new Error(
              "Invoice couldn't be submitted for approval: " +
                (data.message || "-")
            );
          }
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }, [api, id]);

  const getSendToAPSystemPromise = useCallback(() => {
    return new Promise((resolve, reject) => {
      sendBillToAPSystem()
        .then(() => {
          resolve();
          loadBill();
          toaster.success(
            `Invoice${bill?.number ? ` #${bill?.number}` : ""} sent to ${
              company.features.apInboxName || "AP System"
            }`
          );
        })
        .catch((error) => {
          if (error) {
            toaster.danger(error.message);
          }
          reject();
        });
    });
  }, [
    bill?.number,
    company.features.apInboxName,
    loadBill,
    sendBillToAPSystem,
  ]);

  const getPayPromise = useCallback(() => {
    return new Promise((resolve, reject) => {
      payBill()
        .then(() => {
          resolve();
          setTimeout(() => (window.location.href = `${APP_URL}/ap/bills`), 250);
        })
        .catch((error) => {
          if (error) {
            toaster.danger(error.message);
          }
          reject();
        });
    });
  }, [payBill]);

  const getSubmitPromise = useCallback(() => {
    return new Promise((resolve, reject) => {
      submitBill()
        .then(() => {
          toaster.success("Invoice has been submitted for approval");
          resolve();
          setTimeout(() => (window.location.href = `${APP_URL}/ap/bills`), 250);
        })
        .catch((error) => {
          toaster.danger(error.message);
          reject();
        });
    });
  }, [submitBill]);

  const handleDataChange = useCallback(
    (data) => {
      saveToBill(data);
    },
    [saveToBill]
  );

  const handleFormSubmit = useCallback((event) => {
    event.preventDefault();
  }, []);

  const getSendToAPSystemButton = useCallback(
    (props) => (
      <PromiseButton
        {...props}
        flex="1"
        iconBefore={DocumentShareIcon}
        justifyContent="center"
        promiser={getSendToAPSystemPromise}
      >{`Send to ${
        company.features.apInboxName || "AP System"
      }`}</PromiseButton>
    ),
    [company.features.apInboxName, getSendToAPSystemPromise]
  );

  const getPayButton = useCallback(
    (props) => (
      <PromiseButton
        {...props}
        disabled={!bill?.isPayable}
        flex="1"
        iconBefore={DollarIcon}
        justifyContent="center"
        promiser={getPayPromise}
      >{`Pay`}</PromiseButton>
    ),
    [bill?.isPayable, getPayPromise]
  );

  const getSubmitButton = useCallback(
    (props) => (
      <PromiseButton
        {...props}
        disabled={
          bill?.status !== BILL_APPROVE_STATUS.opened &&
          bill?.status !== BILL_APPROVE_STATUS.processingManual &&
          bill?.status !== BILL_APPROVE_STATUS.processingAutomatic
        }
        flex="1"
        iconBefore={TickIcon}
        justifyContent="center"
        promiser={getSubmitPromise}
      >
        {bill?.status !== BILL_APPROVE_STATUS.submitted
          ? "Send for Approval"
          : "Sent for Approval"}
      </PromiseButton>
    ),
    [bill?.status, getSubmitPromise]
  );

  return (
    <Pane alignItems="flex-start" display="flex" flexWrap="wrap">
      <Pane flex="1 1 auto" marginBottom={32} marginX={16} maxWidth="55rem">
        <Pane paddingY={16}>
          <Heading is="h3">Files Preview</Heading>
        </Pane>
        {bill?.files?.map((file, index) => (
          <FilePreview
            data={file["data"]}
            mime={file["mime_type"]}
            key={`file-preview-${index}`}
          />
        ))}
      </Pane>
      <Pane flex="1 1 auto" marginX={16} maxWidth="45rem">
        <Pane
          alignItems="center"
          background="white"
          display="flex"
          justifyContent="space-between"
          paddingY={16}
          position="sticky"
          top={-15}
          zIndex={10}
        >
          <Heading is="h3">Details</Heading>
          <BillStateIndicator
            billState={billState}
            billStatesMap={BILL_STATES}
          />
          <Pane display="inline-flex" alignItems="center">
            {(bill?.paymentInfo?.was_send_to_ap ||
              bill?.paymentInfo?.paid_detailed_status ===
                "sent_to_ap_system") && <APStatus />}
            <StatusBadge status={badgeStatus} />
          </Pane>
        </Pane>
        <Pane>
          <form onSubmit={handleFormSubmit}>
            <Fieldset legend="Vendor">
              <VendorInput
                onDataChange={handleDataChange}
                vendor={bill?.vendor}
              />
            </Fieldset>
            <Fieldset legend="Invoice info">
              <InfoInput
                date={bill?.date}
                amount={bill?.amount}
                due={bill?.due}
                number={bill?.number}
                description={bill?.description}
                onDataChange={handleDataChange}
              />
            </Fieldset>
            <Fieldset legend="Categories">
              <AccountingInput
                amount={bill?.amount}
                onDataChange={handleDataChange}
                splits={bill?.splits}
              />
            </Fieldset>
            <Fieldset legend="Business Owner & 3-way match">
              <MatchInput
                invoiceID={id}
                onDataChange={handleDataChange}
                po={bill?.po}
                bill={bill}
              />
            </Fieldset>
            <Fieldset legend="Approvers">
              <ApproversInput
                approvers={bill?.approvers}
                isEditable={
                  bill?.status !== BILL_APPROVE_STATUS.submitted &&
                  bill?.status !== BILL_APPROVE_STATUS.approved
                }
                onDataChange={handleDataChange}
                status={bill?.status}
              />
            </Fieldset>
            <Pane display="flex" flexWrap="wrap">
              <InvoiceDeleteButton
                iconBefore={TrashIcon}
                invoiceID={id}
                marginRight={8}
              >{`Delete`}</InvoiceDeleteButton>
              {bill?.status === BILL_APPROVE_STATUS.submitted ||
              bill?.status === BILL_APPROVE_STATUS.approved ? (
                <>
                  {getSubmitButton({ appearance: "default", marginRight: 8 })}
                  {company.features.sendToAPInbox &&
                    getSendToAPSystemButton({
                      appearance: "default",
                      marginRight: 8,
                    })}
                  {getPayButton({ appearance: "primary" })}
                </>
              ) : (
                <>
                  {getPayButton({ appearance: "default", marginRight: 8 })}
                  {company.features.sendToAPInbox &&
                    getSendToAPSystemButton({
                      appearance: "default",
                      marginRight: 8,
                    })}
                  {getSubmitButton({ appearance: "primary" })}
                </>
              )}
            </Pane>
          </form>
        </Pane>
      </Pane>
      {dialog}
    </Pane>
  );
};

export default InvoiceFormModule;
